/**
 * SISTEMA DE DISEÑO · "TACTICAL BROADCAST"
 *
 * Fuente única de verdad de la piel del juego: tokens (color, tipografía,
 * espaciado, geometría, elevación) y primitivas reutilizables (`.tb-*`) que
 * consumen TODAS las pantallas — menú, panel de partida, lobby, overlays y
 * avisos. Antes cada namespace CSS (`.vpm-`, `.lob-`, `.ph-`, `.nm-`, `.lg-`,
 * `.cn-`) inventaba sus propios valores; aquí se definen una vez.
 *
 * ## El lenguaje
 *
 * Retransmisión deportiva táctica: la claridad agresiva de un shooter táctico
 * con la energía cinética de un juego de fútbol. En concreto:
 *
 *  - **Fondo obsidiana** y contraste extremo. Nada compite con la cancha.
 *  - **Duotono neón**: cian eléctrico y magenta/rojo fuego. Son los MISMOS dos
 *    colores de los equipos, así que la interfaz y el campo hablan el mismo
 *    idioma: lo cian es "turquesa/defensa", lo magenta es "rojo/ataque".
 *  - **Geometría achaflanada**: nada de esquinas redondeadas. Cortes a 45° con
 *    `clip-path`, asimétricos (esquina superior izquierda e inferior derecha),
 *    para que cada panel apunte hacia delante.
 *  - **Oblicuidad**: los elementos de acción van inclinados (`skewX`) con su
 *    contenido contra-inclinado, de modo que el texto queda recto y legible.
 *    Los paneles de LECTURA no se inclinan: un bloque de texto girado 12° es
 *    una postal bonita y un texto que no se lee. La energía la ponen las
 *    barras, los biseles y los acentos diagonales.
 *  - **Microdatos**: códigos técnicos (`[SYS_01]`, `// READY`) en las esquinas.
 *    Son decorativos, así que van con `aria-hidden` implícito (pseudoelementos)
 *    y **desaparecen en móvil**, donde cada píxel es para lo que importa.
 *
 * ## Responsivo de verdad
 *
 * Todo el dimensionado va en `rem`/`clamp()`/`%`/`vw`, nunca en píxeles fijos,
 * multiplicado por `--v-scale` (la escala de texto de accesibilidad). Los
 * puntos de corte son tres y están documentados abajo. El puntero decide el
 * tamaño mínimo táctil (`pointer: coarse` → 2.75rem) y si hay efectos de hover
 * (`hover: hover and pointer: fine`), no el ancho de la ventana: un portátil
 * táctil de 1400 px necesita botones grandes igual que un móvil.
 */

/* ---------------------------------------------------------------- tokens -- */

/**
 * Puntos de corte. En CSS no se pueden usar variables dentro de `@media`, así
 * que los valores viven aquí como constantes documentadas y se repiten en las
 * reglas; estos son los ÚNICOS tres que usa el proyecto entero.
 *
 * - `sm` (<48rem / 768px): móvil vertical. Una columna, navegación inferior.
 * - `md` (48–64rem / 768–1024px): tablet y móvil apaisado. Dos columnas donde
 *   caben, cabecera compacta.
 * - `lg` (>64rem / 1024px): escritorio. Paneles simultáneos y hover.
 *
 * Además, `(max-height: 32rem) and (orientation: landscape)` reconoce el móvil
 * apaisado real (poca altura), que es un caso distinto de "pantalla estrecha".
 */
export const BREAKPOINTS = { sm: 768, md: 1024, lg: 1440 } as const;

const TOKENS = `:root{
  /* ---- superficie (obsidiana / grafito mate) ---- */
  --tb-void:#05070b;
  --tb-abyss:#080c12;
  --tb-panel:#0d131a;
  --tb-panel-2:#121a23;
  --tb-panel-3:#18222d;
  --tb-line:rgba(122,160,190,0.16);
  --tb-line-2:rgba(122,160,190,0.28);

  /* ---- duotono neón (= colores de equipo) ---- */
  --tb-cyan:#22e6ff;
  --tb-cyan-2:#0fb9d6;
  --tb-cyan-dim:rgba(34,230,255,0.14);
  --tb-magenta:#ff2e63;
  --tb-magenta-2:#e01a4d;
  --tb-magenta-dim:rgba(255,46,99,0.14);
  --tb-warn:#ffc93c;
  --tb-ok:#2effa8;
  /* Rojo de ADVERTENCIA. Deliberadamente distinto del magenta de marca: el
     magenta es "esto es lo principal" y este es "esto te saca de la partida". */
  --tb-danger:#ff3b30;
  --tb-danger-2:#c81e14;

  /* ---- texto ---- */
  --tb-text:#eef4f8;
  --tb-text-2:#b9c9d6;
  --tb-muted:#6d8092;

  /* ---- geometría ---- */
  --tb-chamfer:0.75rem;        /* corte a 45° de los paneles */
  --tb-chamfer-sm:0.45rem;     /* corte de botones y chips */
  --tb-skew:-9deg;             /* inclinación de los elementos de acción */
  --tb-hair:1px;               /* grosor del filo de neón */

  /* ---- espaciado (escala de 4) ---- */
  --sp-1:0.25rem; --sp-2:0.5rem; --sp-3:0.75rem; --sp-4:1rem;
  --sp-5:1.5rem;  --sp-6:2rem;   --sp-7:3rem;

  /* ---- tipografía: escala fluida, ya multiplicada por la escala de a11y ----
     clamp(mínimo, preferido con vw, máximo): el texto "respira" con la ventana
     en vez de saltar en cada punto de corte. */
  --fs-micro:calc(clamp(0.5rem, 0.46rem + 0.18vw, 0.62rem) * var(--v-scale,1));
  --fs-xs:calc(clamp(0.62rem, 0.58rem + 0.2vw, 0.75rem) * var(--v-scale,1));
  --fs-sm:calc(clamp(0.72rem, 0.66rem + 0.3vw, 0.88rem) * var(--v-scale,1));
  --fs-md:calc(clamp(0.82rem, 0.74rem + 0.4vw, 1rem) * var(--v-scale,1));
  --fs-lg:calc(clamp(1rem, 0.86rem + 0.7vw, 1.35rem) * var(--v-scale,1));
  --fs-xl:calc(clamp(1.35rem, 1rem + 1.8vw, 2.4rem) * var(--v-scale,1));
  --fs-display:calc(clamp(2rem, 1rem + 6vw, 4.5rem) * var(--v-scale,1));

  /* Familia industrial condensada. No se descarga ninguna fuente: la pila cae
     en la más estrecha y pesada que tenga el sistema, y la cursiva + el
     tracking hacen el resto del carácter "stencil". Una fuente incrustada
     costaría ~80 KB en la primera carga de un juego que presume de arrancar
     al instante. */
  --tb-font:"Archivo Black","Franklin Gothic Bold","Arial Narrow","Haettenschweiler",
    "Impact","Segoe UI",system-ui,sans-serif;
  --tb-font-ui:"Segoe UI Semibold","Segoe UI",system-ui,-apple-system,sans-serif;
  --tb-font-mono:"JetBrains Mono","SF Mono",ui-monospace,"Cascadia Mono",Consolas,monospace;

  /* ---- elevación y luz ---- */
  --tb-glow-cyan:0 0 0.75rem rgba(34,230,255,0.45);
  --tb-glow-magenta:0 0 0.75rem rgba(255,46,99,0.45);
  --tb-glow-danger:0 0 0.75rem rgba(255,59,48,0.5);
  --tb-lift:0 1.5rem 3rem rgba(0,0,0,0.55);
  --tb-blur:blur(1.25rem) saturate(1.3);

  /* ---- compatibilidad ----
     Los nombres antiguos (--v-*) se mantienen como ALIAS del duotono nuevo: hay
     CSS de componentes que todavía los usa y no tiene por qué enterarse del
     cambio de marca. */
  --v-bg:var(--tb-void);
  --v-panel:var(--tb-panel);
  --v-panel2:var(--tb-panel-2);
  --v-panel-a:var(--tb-panel);
  --v-panel-b:var(--tb-abyss);
  --v-red:var(--tb-magenta);
  --v-red-hi:#ff5480;
  --v-teal:var(--tb-cyan);
  --v-teal2:var(--tb-cyan-2);
  --v-text:var(--tb-text);
  --v-muted:var(--tb-muted);
  --v-warn:var(--tb-warn);
  --v-line:var(--tb-line);
  --v-hover:var(--tb-panel-3);
  --v-scale:1;
}`;

/* ------------------------------------------------------------ primitivas -- */

const SYSTEM = `
/* =============================== BASE ================================= */
.tb, .tb-root{font-family:var(--tb-font-ui);color:var(--tb-text);}

/* Corte a 45°: asimétrico (arriba-izquierda y abajo-derecha) para que el panel
   "apunte". Se aplica con clip-path, así que funciona igual en cualquier
   tamaño y no necesita imágenes. */
.tb-cut{--c:var(--tb-chamfer);
  clip-path:polygon(var(--c) 0, 100% 0, 100% calc(100% - var(--c)), calc(100% - var(--c)) 100%, 0 100%, 0 var(--c));}
.tb-cut-sm{--c:var(--tb-chamfer-sm);
  clip-path:polygon(var(--c) 0, 100% 0, 100% calc(100% - var(--c)), calc(100% - var(--c)) 100%, 0 100%, 0 var(--c));}
/* Corte simétrico (las cuatro esquinas), para tarjetas y modales. */
.tb-cut-4{--c:var(--tb-chamfer);
  clip-path:polygon(var(--c) 0, calc(100% - var(--c)) 0, 100% var(--c), 100% calc(100% - var(--c)),
    calc(100% - var(--c)) 100%, var(--c) 100%, 0 calc(100% - var(--c)), 0 var(--c));}

/* Filo de neón que SIGUE al corte. Un borde normal se lo come el clip-path;
   esto pinta el color en un pseudoelemento y le quita el centro con una
   máscara, así que queda una línea de 1px por todo el contorno, diagonales
   incluidas. */
.tb-edge{position:relative;}
.tb-edge::before{content:"";position:absolute;inset:0;padding:var(--tb-hair);pointer-events:none;
  background:var(--edge,var(--tb-line-2));clip-path:inherit;
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;}

/* Cristal esmerilado para fondos de menú. */
.tb-glass{background:linear-gradient(158deg,rgba(18,26,35,0.82),rgba(8,12,18,0.9));
  backdrop-filter:var(--tb-blur);-webkit-backdrop-filter:var(--tb-blur);}

/* Textura: fibra de carbono mate + barrido de metal cepillado. Son gradientes,
   no imágenes: cero peticiones y escala perfecta. */
.tb-carbon{background-image:
  repeating-linear-gradient(45deg,rgba(255,255,255,0.020) 0 1px,transparent 1px 3px),
  repeating-linear-gradient(-45deg,rgba(0,0,0,0.22) 0 1px,transparent 1px 3px);}
.tb-brushed{background-image:repeating-linear-gradient(100deg,
  rgba(255,255,255,0.05) 0 1px,rgba(255,255,255,0) 1px 4px);}

/* Rejilla táctica del fondo (se ve entre paneles, muy tenue). */
.tb-grid::after{content:"";position:absolute;inset:0;pointer-events:none;opacity:.5;
  background-image:linear-gradient(rgba(34,230,255,0.045) 1px,transparent 1px),
    linear-gradient(90deg,rgba(34,230,255,0.045) 1px,transparent 1px);
  background-size:3.5rem 3.5rem;
  -webkit-mask-image:radial-gradient(120% 90% at 50% 0%,#000,transparent 75%);
  mask-image:radial-gradient(120% 90% at 50% 0%,#000,transparent 75%);}

/* =============================== TIPO ================================= */
.tb-display{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-display);line-height:0.92;letter-spacing:0.02em;font-weight:900;}
.tb-h1{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-xl);letter-spacing:0.04em;font-weight:900;line-height:1.05;}
.tb-h2{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-lg);letter-spacing:0.06em;font-weight:900;}
.tb-label{text-transform:uppercase;font-size:var(--fs-xs);letter-spacing:0.22em;
  font-weight:800;color:var(--tb-muted);}
.tb-body{font-size:var(--fs-sm);line-height:1.55;color:var(--tb-text-2);letter-spacing:0.01em;}

/* Microdatos técnicos: código en la esquina de un panel. Decorativos, así que
   se apagan en móvil (donde el espacio es para el contenido) y con
   "prefers-reduced-motion"/lectores no aportan nada porque son pseudoelementos. */
.tb-meta{position:relative;}
.tb-meta::after{content:attr(data-code);position:absolute;right:var(--sp-3);top:var(--sp-2);
  font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.14em;
  color:var(--tb-muted);opacity:.55;pointer-events:none;white-space:nowrap;}
.tb-meta.bl::after{right:auto;top:auto;left:var(--sp-3);bottom:var(--sp-2);}

/* ============================== ACCIÓN ================================ */
/* Botón: cara inclinada + contenido contra-inclinado (el texto queda recto).
   El área táctil sigue siendo el rectángulo del <button>, así que la
   inclinación no le quita superficie al dedo. */
.tb-btn{position:relative;display:inline-flex;align-items:center;justify-content:center;
  gap:var(--sp-2);min-height:2.75rem;padding:0.8em 1.6em;border:0;cursor:pointer;
  background:none;color:var(--tb-text);font-family:var(--tb-font-ui);font-size:var(--fs-sm);
  font-weight:800;letter-spacing:0.16em;text-transform:uppercase;isolation:isolate;
  transition:color .14s ease,transform .14s ease;}
/* La cara va INSETADA en horizontal: al inclinarla, sus esquinas se salen del
   rectángulo del botón y quedarían cortadas. Con el margen, el paralelogramo
   se ve entero y el área táctil sigue siendo el rectángulo completo. */
.tb-btn::before{content:"";position:absolute;inset:0 0.4rem;z-index:-1;transform:skewX(var(--tb-skew));
  clip-path:polygon(var(--tb-chamfer-sm) 0,100% 0,100% calc(100% - var(--tb-chamfer-sm)),
    calc(100% - var(--tb-chamfer-sm)) 100%,0 100%,0 var(--tb-chamfer-sm));
  background:linear-gradient(160deg,var(--tb-panel-3),var(--tb-panel));
  box-shadow:inset 0 0 1.2rem rgba(0,0,0,0.55);
  background-image:linear-gradient(160deg,var(--tb-panel-3),var(--tb-panel)),
    repeating-linear-gradient(45deg,rgba(255,255,255,0.02) 0 1px,transparent 1px 3px);
  transition:box-shadow .14s ease,background .14s ease,filter .14s ease;}
/* MARCO del botón, biseles incluidos.
   Antes el borde era una sombra interior (box-shadow inset), y una sombra
   interior NO conoce el recorte: dibuja el marco del RECTÁNGULO y el clip-path
   se come justo los dos cortes a 45°, así que las esquinas diagonales quedaban
   abiertas (bisel sin línea, como un botón mal recortado). Este pseudoelemento
   pinta el color a sangre y le quita el centro con una máscara, de modo que la
   línea de 1px sigue el contorno REAL, diagonales incluidas. Es la misma
   técnica de .tb-edge, aquí aplicada a la cara inclinada del botón. */
.tb-btn::after{content:"";position:absolute;inset:0 0.4rem;z-index:-1;pointer-events:none;
  transform:skewX(var(--tb-skew));
  clip-path:polygon(var(--tb-chamfer-sm) 0,100% 0,100% calc(100% - var(--tb-chamfer-sm)),
    calc(100% - var(--tb-chamfer-sm)) 100%,0 100%,0 var(--tb-chamfer-sm));
  padding:var(--tb-hair);background:var(--btn-edge,var(--tb-line-2));
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;transition:background .14s ease;}
.tb-btn>*{position:relative;}
.tb-btn:disabled{opacity:.38;cursor:not-allowed;}

.tb-btn.primary{color:#05070b;--btn-edge:rgba(255,255,255,0.35);}
.tb-btn.primary::before{background:linear-gradient(160deg,var(--tb-magenta),var(--tb-magenta-2));
  box-shadow:var(--tb-glow-magenta);}
.tb-btn.accent{color:#05070b;--btn-edge:rgba(255,255,255,0.35);}
.tb-btn.accent::before{background:linear-gradient(160deg,var(--tb-cyan),var(--tb-cyan-2));
  box-shadow:var(--tb-glow-cyan);}
/* Acción DESTRUCTIVA (abandonar la sala, expulsar): rojo de advertencia, que no
   es el magenta de marca. La diferencia importa — si el botón de irse tiene el
   mismo color que el de jugar, el color deja de significar nada. */
.tb-btn.danger{color:#fff;--btn-edge:rgba(255,255,255,0.42);}
.tb-btn.danger::before{background:linear-gradient(160deg,var(--tb-danger),var(--tb-danger-2));
  box-shadow:var(--tb-glow-danger);}
.tb-btn.ghost{--btn-edge:var(--tb-line);}
.tb-btn.ghost::before{background:rgba(255,255,255,0.02);}

/* Hover SOLO donde hay ratón de verdad. En una pantalla táctil el "hover" se
   queda pegado tras el toque y el botón parece encendido para siempre. */
@media (hover:hover) and (pointer:fine){
  .tb-btn:hover:not(:disabled){color:#fff;--btn-edge:var(--tb-cyan);}
  .tb-btn:hover:not(:disabled)::before{filter:brightness(1.18);
    box-shadow:0 0 1.4rem rgba(34,230,255,0.35);}
  .tb-btn.primary:hover:not(:disabled){color:#05070b;--btn-edge:rgba(255,255,255,0.6);}
  .tb-btn.primary:hover:not(:disabled)::before{box-shadow:0 0 1.6rem rgba(255,46,99,0.6);}
  .tb-btn.accent:hover:not(:disabled){color:#05070b;--btn-edge:rgba(255,255,255,0.6);}
  .tb-btn.accent:hover:not(:disabled)::before{box-shadow:0 0 1.6rem rgba(34,230,255,0.6);}
  .tb-btn.danger:hover:not(:disabled){color:#fff;--btn-edge:rgba(255,255,255,0.7);}
  .tb-btn.danger:hover:not(:disabled)::before{box-shadow:0 0 1.6rem rgba(255,59,48,0.65);}
  /* Los microdatos "se activan" al pasar el ratón: es el guiño de consola. */
  .tb-meta:hover::after{opacity:1;color:var(--tb-cyan);}
}
.tb-btn:active:not(:disabled){transform:translateY(1px);}
.tb-btn:focus-visible{outline:2px solid var(--tb-cyan);outline-offset:3px;}

/* Dedo: todo lo pulsable crece y se separa. Se decide por el PUNTERO, no por el
   ancho de la ventana. */
@media (pointer:coarse){
  .tb-btn{min-height:3rem;padding:0.95em 1.5em;letter-spacing:0.12em;}
  .tb-meta::after{display:none;}
}

/* ============================== PANELES =============================== */
.tb-panel{position:relative;background:linear-gradient(158deg,var(--tb-panel),var(--tb-abyss));
  box-shadow:var(--tb-lift);}
/* Barra de acento diagonal del borde izquierdo: la firma del lenguaje. */
.tb-panel>.tb-spine{position:absolute;left:0;top:0;bottom:0;width:0.28rem;
  background:linear-gradient(180deg,var(--tb-magenta),var(--tb-cyan));opacity:.9;
  clip-path:polygon(0 0,100% 0.6rem,100% 100%,0 calc(100% - 0.6rem));}
.tb-head{display:flex;align-items:center;gap:var(--sp-4);padding:var(--sp-4) var(--sp-5);
  border-bottom:1px solid var(--tb-line);
  background:linear-gradient(100deg,var(--tb-magenta-dim),transparent 46%);}
.tb-foot{display:flex;gap:var(--sp-3);padding:var(--sp-4) var(--sp-5);
  border-top:1px solid var(--tb-line);background:rgba(0,0,0,0.28);}

/* Tarjeta poligonal apilable (la unidad de contenido en móvil). */
.tb-card{position:relative;background:linear-gradient(158deg,rgba(19,27,36,0.9),rgba(9,13,19,0.9));
  padding:var(--sp-4);}

/* ============================ NAVEGACIÓN ============================== */
/* Carril de pestañas: vertical en escritorio, barra inferior en móvil. El
   marcado es el MISMO; lo único que cambia es cómo lo coloca el CSS. */
.tb-rail{display:flex;flex-direction:column;gap:var(--sp-1);padding:var(--sp-4) var(--sp-2);
  flex:0 0 clamp(9rem,14vw,12rem);border-right:1px solid var(--tb-line);background:rgba(0,0,0,0.22);}
.tb-tab{position:relative;display:flex;align-items:center;gap:var(--sp-2);min-height:2.75rem;
  padding:0.7em 1em;border:0;background:none;cursor:pointer;text-align:left;
  color:var(--tb-muted);font-family:var(--tb-font-ui);font-size:var(--fs-sm);font-weight:800;
  letter-spacing:0.16em;text-transform:uppercase;transition:color .12s ease,background .12s ease;}
.tb-tab::before{content:"";position:absolute;left:0;top:.4rem;bottom:.4rem;width:0.18rem;
  background:transparent;transition:background .12s ease,box-shadow .12s ease;}
.tb-tab.active{color:var(--tb-text);background:linear-gradient(90deg,var(--tb-magenta-dim),transparent 70%);}
.tb-tab.active::before{background:var(--tb-magenta);box-shadow:var(--tb-glow-magenta);}
@media (hover:hover) and (pointer:fine){
  .tb-tab:hover{color:var(--tb-text);background:rgba(255,255,255,0.03);}
  .tb-tab:hover::before{background:var(--tb-cyan);}
}

/* ============================= CONTROLES ============================== */
.tb-input{width:100%;box-sizing:border-box;padding:0.85em 1em;background:rgba(5,8,12,0.75);
  border:1px solid var(--tb-line);color:var(--tb-text);font:inherit;font-size:var(--fs-sm);
  letter-spacing:0.06em;outline:none;transition:border-color .12s ease,box-shadow .12s ease;}
.tb-input:focus{border-color:var(--tb-cyan);box-shadow:0 0 0 1px var(--tb-cyan),0 0 1rem rgba(34,230,255,0.25);}
select.tb-input{appearance:none;-webkit-appearance:none;
  background-image:linear-gradient(45deg,transparent 50%,var(--tb-cyan) 50%),
    linear-gradient(135deg,var(--tb-cyan) 50%,transparent 50%);
  background-position:calc(100% - 1.15rem) 55%,calc(100% - 0.8rem) 55%;
  background-size:0.35rem 0.35rem,0.35rem 0.35rem;background-repeat:no-repeat;padding-right:2.4rem;}

/* Segmentado: una fila de opciones excluyentes. En móvil se apila si no cabe. */
.tb-seg{display:flex;gap:var(--sp-2);flex-wrap:wrap;}
.tb-seg>button{flex:1 1 6rem;}

/* Interruptor. */
.tb-switch{display:flex;align-items:center;gap:var(--sp-3);min-height:2.75rem;cursor:pointer;
  font-size:var(--fs-sm);font-weight:800;letter-spacing:0.1em;text-transform:uppercase;
  color:var(--tb-text-2);width:fit-content;}
.tb-switch i{position:relative;width:2.9rem;height:1.5rem;flex:none;background:rgba(5,8,12,0.85);
  border:1px solid var(--tb-line);transform:skewX(var(--tb-skew));transition:border-color .12s ease;}
.tb-switch i::after{content:"";position:absolute;top:0.15rem;left:0.15rem;width:1rem;height:1rem;
  background:var(--tb-muted);transition:transform .14s ease,background .14s ease,box-shadow .14s ease;}
.tb-switch.on{color:var(--tb-text);}
.tb-switch.on i{border-color:var(--tb-cyan);}
.tb-switch.on i::after{transform:translateX(1.35rem);background:var(--tb-cyan);box-shadow:var(--tb-glow-cyan);}

/* Deslizador. */
.tb-range{-webkit-appearance:none;appearance:none;width:100%;height:0.45rem;outline:none;
  background:linear-gradient(90deg,var(--tb-magenta),var(--tb-cyan));margin:var(--sp-3) 0;
  clip-path:polygon(0.3rem 0,100% 0,calc(100% - 0.3rem) 100%,0 100%);}
.tb-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:1.3rem;height:1.3rem;
  background:var(--tb-text);cursor:pointer;border:2px solid var(--tb-void);
  transform:skewX(var(--tb-skew));box-shadow:0 0 0.7rem rgba(34,230,255,0.65);}
.tb-range::-moz-range-thumb{width:1.3rem;height:1.3rem;background:var(--tb-text);cursor:pointer;
  border:2px solid var(--tb-void);box-shadow:0 0 0.7rem rgba(34,230,255,0.65);}

/* Marco que sigue al bisel en un control CUADRADO recortado (botones pequeños
   de icono, teclas, el botón de pausa). Mismo problema y misma solución que en
   .tb-btn::after: un border normal se lo come el recorte y las esquinas a 45°
   quedan sin línea. Se usa junto a .tb-cut-sm. */
.tb-bevel{position:relative;border:0;}
.tb-bevel::after{content:"";position:absolute;inset:0;pointer-events:none;clip-path:inherit;
  padding:var(--tb-hair);background:var(--edge,var(--tb-line));
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;transition:background .12s ease;}

/* Chip / ficha de jugador. */
.tb-chip{display:flex;align-items:center;gap:var(--sp-2);padding:0.6em 0.9em;min-height:2.5rem;
  background:linear-gradient(160deg,var(--tb-panel-2),var(--tb-panel));
  font-size:var(--fs-sm);font-weight:700;letter-spacing:0.06em;}

/* Badge de estado. */
.tb-badge{display:inline-flex;align-items:center;gap:var(--sp-2);padding:0.35em 0.7em;
  font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.16em;
  text-transform:uppercase;color:var(--tb-muted);border:1px solid var(--tb-line);}
.tb-badge.cyan{color:var(--tb-cyan);border-color:rgba(34,230,255,0.4);}
.tb-badge.magenta{color:var(--tb-magenta);border-color:rgba(255,46,99,0.4);}
.tb-badge.warn{color:var(--tb-warn);border-color:rgba(255,201,60,0.4);}

/* ============================== SCROLL ================================ */
.tb-scroll{overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;
  scrollbar-color:var(--tb-line-2) transparent;}
.tb-scroll::-webkit-scrollbar{width:0.5rem;height:0.5rem;}
.tb-scroll::-webkit-scrollbar-thumb{background:var(--tb-line-2);}
.tb-scroll::-webkit-scrollbar-track{background:transparent;}

/* ====================== ADAPTACIÓN POR PANTALLA ======================= */

/* --- MÓVIL VERTICAL (<48rem): una columna y navegación abajo --- */
@media (max-width:767.98px){
  :root{--tb-chamfer:0.6rem;--tb-chamfer-sm:0.35rem;}
  /* El carril de pestañas pasa a ser una barra inferior, con las pestañas en
     fila y desplazables. Mismo marcado: solo cambia la dirección del flex. */
  .tb-rail{flex-direction:row;order:99;border-right:0;border-top:1px solid var(--tb-line);
    padding:var(--sp-2) var(--sp-4) var(--sp-2) var(--sp-2);gap:var(--sp-2);overflow-x:auto;flex:0 0 auto;
    scroll-snap-type:x proximity;background:rgba(0,0,0,0.5);}
  .tb-rail .tb-tab{flex:0 0 auto;justify-content:center;text-align:center;padding:0.6em 0.9em;
    letter-spacing:0.08em;scroll-snap-align:center;}
  .tb-tab::before{left:.4rem;right:.4rem;top:auto;bottom:0;width:auto;height:0.18rem;}
  .tb-seg>button{flex:1 1 100%;}
}

/* --- MÓVIL APAISADO: poca altura, hay que comprimir en vertical --- */
@media (max-height:32rem) and (orientation:landscape){
  :root{--sp-5:1rem;--sp-6:1.25rem;--sp-7:1.5rem;}
  .tb-head{padding:var(--sp-2) var(--sp-4);}
  .tb-foot{padding:var(--sp-2) var(--sp-4);}
  .tb-btn{min-height:2.5rem;padding:0.6em 1.2em;}
  /* Apaisado el carril vuelve a ser lateral: hay ancho de sobra y falta alto. */
  .tb-rail{flex-direction:column;order:0;border-top:0;border-right:1px solid var(--tb-line);
    flex:0 0 clamp(6.5rem,18vw,9rem);overflow-y:auto;overflow-x:hidden;}
  .tb-rail .tb-tab{justify-content:flex-start;text-align:left;}
  .tb-tab::before{left:0;right:auto;top:.3rem;bottom:.3rem;width:0.18rem;height:auto;}
}

/* --- ESCRITORIO (>64rem): densidad, columnas simultáneas --- */
@media (min-width:1024px){
  .tb-only-mobile{display:none !important;}
}
@media (max-width:1023.98px){
  .tb-only-desktop{display:none !important;}
}

/* Menos movimiento para quien lo pide (y para quien se marea con paneles que
   entran volando). */
@media (prefers-reduced-motion:reduce){
  *,*::before,*::after{animation-duration:.001ms !important;animation-iteration-count:1 !important;
    transition-duration:.001ms !important;}
}
`;

/**
 * Inyecta tokens + primitivas una sola vez, ANTES que cualquier hoja de
 * componente (para que `var(--tb-*)` ya resuelva cuando se evalúan).
 *
 * Se conserva el nombre histórico `injectThemeTokens` como alias en
 * `src/theme.ts` para no tocar los imports de cada componente.
 */
export function injectDesignSystem(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById("tb-system")) return;
  const style = document.createElement("style");
  style.id = "tb-system";
  style.textContent = TOKENS + SYSTEM;
  document.head.insertBefore(style, document.head.firstChild);
}

/**
 * Aplica un microdato técnico a un elemento (`[SYS_01]`, `// READY`…).
 *
 * Va por atributo y lo pinta el CSS (`.tb-meta::after`), así que en móvil se
 * apaga solo y nunca estorba al contenido real.
 */
export function meta(el: HTMLElement, code: string, corner: "tr" | "bl" = "tr"): void {
  el.classList.add("tb-meta");
  if (corner === "bl") el.classList.add("bl");
  el.dataset.code = code;
}
